import { ApplicationRef, ChangeDetectionStrategy, Component, DestroyRef, OnInit, PLATFORM_ID, Renderer2, DOCUMENT, ViewChild, ViewContainerRef, inject, signal } from '@angular/core';
import { temaOscuroInicial } from './utils/tema-inicial';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';

import { StorageService } from './services/storage/storage.service';
import { UtilsService } from './services/utils/utils.service';
import { SessionService } from './services/session/session.service';
import { Router, NavigationEnd } from '@angular/router';
import { MetadataService } from './services/metadata/metadata.service';
import { ConsentService } from './services/consent/consent.service';
import { PageTrackerService } from './services/analytics/page-tracker.service';
import { EventService } from './services/event/event.service';
import { isPlatformBrowser } from '@angular/common';
import { environment } from "src/environments/environment.prod";
import { CATEGORIAS, CATEGORIAS_DESTACADAS, TIENDAS } from './data/menu-catalogo';
import { CLAVE_ASISTENTE, activacionAsistente } from './utils/asistente-activacion';
import { apiNotificaciones, debePedirToken, sincronizarTokenNotificaciones } from './utils/permiso-notificaciones';
// Firebase imports se cargan dinámicamente solo cuando se necesitan (lazy loading)

/** localStorage: último token FCM que la API aceptó para este navegador. */
export const CLAVE_TOKEN_FCM_ENVIADO = 'mc-fcm-token-enviado';

/**
 * Registra el token de notificaciones push del usuario con sesión en la API
 * (`PATCH fcmtokens`, vía `session.mc.setFcmToken`). Devuelve `true` si se envió.
 *
 * Hasta octubre de 2026 el token se mandaba a un «topic» de Google (`iid.googleapis.com`)
 * con una server key incrustada en el cliente, y la API no sabía a qué dispositivo avisar
 * de las alertas de cada usuario. Se recuerda el último token aceptado para no repetir la
 * petición en cada carga; se reenvía cuando el token cambia (indicando el anterior para
 * que la API lo retire) y, con `forzar`, al iniciar sesión. Sin usuario no se manda nada:
 * los tokens van ligados a una cuenta. Los fallos no son fatales, pero se avisan por
 * consola en vez de tragarse en silencio.
 */
export async function enviarTokenFcm(
  mc: { user?: unknown; setFcmToken: (nuevo: string, proveedor: string, viejo?: string | null) => Promise<any> } | null,
  token: string,
  { forzar = false, almacen = typeof localStorage === 'object' ? localStorage : null }: { forzar?: boolean; almacen?: Storage | null } = {},
): Promise<boolean> {
  if (!token || !mc?.user) return false;

  let anterior: string | null = null;
  try {
    anterior = almacen?.getItem(CLAVE_TOKEN_FCM_ENVIADO) ?? null;
  } catch {
    // Sin almacén (modo privado, almacenamiento bloqueado): se envía siempre.
  }

  if (!forzar && anterior === token) return false;

  try {
    const resp = await mc.setFcmToken(token, 'FCM', anterior && anterior !== token ? anterior : null);

    if (resp?.ok) {
      try { almacen?.setItem(CLAVE_TOKEN_FCM_ENVIADO, token); } catch { /* sin almacén: se reenviará en la próxima carga */ }
      return true;
    }

    console.warn('[push] la API no aceptó el token FCM', resp);
  } catch (error) {
    console.warn('[push] no se pudo registrar el token FCM en la API', error);
  }

  return false;
}

@Component({
    selector: 'app-root',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss'],
    // SE QUEDA EN Default, y no es despiste. Este es el componente RAÍZ: con él en OnPush,
    // su subárbol —o sea, toda la aplicación colgando de <ion-router-outlet>— deja de
    // comprobarse salvo que algo marque la raíz como sucia. Medido: la home cargaba sus
    // chollos tras el await y no se pintaba NINGUNO (0 tarjetas contra las 10 de
    // producción). Hasta que las páginas grandes no tengan su estado en señales, la raíz
    // tiene que seguir en Default.
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class AppComponent implements OnInit {
  private storage = inject(StorageService);
  private utils = inject(UtilsService);
  /**
   * Se inyecta aquí y no donde se usa para que el cliente de la API exista antes de que
   * ninguna página pida datos. Hasta el lote 28e3 lo construía `UtilsService` en su
   * constructor, y era este mismo `inject` el que lo disparaba de rebote; ahora el dueño
   * es SessionService y hay que pedirlo explícitamente: la toolbar y el guard que lo
   * inyectan no están en todas las rutas, ni en el render del servidor.
   */
  private session = inject(SessionService);
  private router = inject(Router);
  private meta = inject(MetadataService);
  private consent = inject(ConsentService);
  private pageTracker = inject(PageTrackerService);
  private event = inject(EventService);
  private platformId = inject(PLATFORM_ID);
  private renderer = inject(Renderer2);
  private document = inject<Document>(DOCUMENT);
  private destroyRef = inject(DestroyRef);




  /** Catálogos del menú; viven en data/menu-catalogo.ts. */
  readonly bigCategories = CATEGORIAS_DESTACADAS;
  readonly categories = CATEGORIAS;
  readonly stores = TIENDAS;

  readonly url = signal<string>(undefined);

  /** Lo pinta la plantilla y cambia al decidir sobre las cookies. */
  readonly showCookies = signal(false);

  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private firebaseInitialized = false;
  /** Token FCM de este navegador, cuando ya se ha obtenido; se reenvía al iniciar sesión. */
  private fcmToken: string | null = null;

  /**
   * Incidente 12/09/2026 (M2): index.html marca el body con `mc-con-sesion` cuando hay
   * sesión guardada, para que el SSR anónimo no enseñe «Regístrate/Inicia sesión» a un
   * usuario logueado mientras Angular arranca. La marca se retira cuando la aplicación
   * está ESTABLE, que es después de que la hidratación limpie las vistas deshidratadas
   * que no reclamó nadie (ese botón del SSR, que convive unos 250 ms con los controles
   * logueados): retirarla antes, en afterNextRender, reintroducía el parpadeo. Y el
   * `inject` va aquí, en el constructor, no dentro del callback (NG0203). Si la sesión
   * resultó revocada, la toolbar pinta un botón nuevo, sin `jsaction`, que la regla de
   * global.scss no esconde ni aunque la marca siguiera puesta.
   */
  constructor() {
    if (this.isBrowser) {
      const documento = this.document;
      inject(ApplicationRef).whenStable().then(() => {
        documento.body.classList.remove('mc-con-sesion');
        // `mc-listo` es la señal que espera el cargador de etiquetas de index.html
        // (GTM + gtag + Meta + TikTok). Hasta el 13/09/2026 esas etiquetas se
        // enganchaban a pointerdown/keydown/touchstart/scroll, los MISMOS eventos con
        // los que arranca el bundle: el primer toque disparaba a la vez 268 KB de JS
        // propio y 653 KB de terceros y competían por la red y por el hilo principal.
        // Medido en portada/móvil: del gesto a interactiva 4.825 → 2.354 ms y TBT
        // posterior al gesto 1.038 → 259 ms con las etiquetas fuera de esa ventana.
        // Se emite AQUÍ, en el mismo whenStable() que retira `mc-con-sesion`, porque
        // es el primer instante en el que la hidratación ya no compite con nadie.
        documento.defaultView?.dispatchEvent(new Event('mc-listo'));
      });
    }
  }

  /** Ancla del asistente de voz oculto (odd/tasks/asistente-voz.md, S5). */
  @ViewChild('anclaAsistente', { read: ViewContainerRef, static: true }) anclaAsistente?: ViewContainerRef;

  /**
   * Solo con `?asistente=<código>` (o si este navegador ya lo activó) se descarga y pinta el
   * asistente; a los demás no les cuesta nada: va en su propio trozo de JS.
   */
  private async montarAsistente() {
    let guardado: string | null = null;
    try { guardado = localStorage.getItem(CLAVE_ASISTENTE); } catch { /* sin almacenamiento */ }
    const { activo, guardar } = activacionAsistente(location.search, guardado);
    try {
      if (guardar === '1') localStorage.setItem(CLAVE_ASISTENTE, '1');
      else if (guardar === null) localStorage.removeItem(CLAVE_ASISTENTE);
    } catch { /* sin almacenamiento */ }
    if (!activo || !this.anclaAsistente) return;
    const { AsistenteVozComponent } = await import('./components/asistente-voz/asistente-voz.component');
    this.anclaAsistente.createComponent(AsistenteVozComponent);
  }

  async initializeApp() {
    // Firebase se carga dinámicamente en refrescarTokenFcm() solo cuando se necesita
    // Esto ahorra ~200-300KB de JavaScript inicial
    // this.translate.setDefaultLang('es');
    this.pageTracker.init();
    this.getPageName();
  }

  async ngOnInit() {
    // También en SERVIDOR: el banner de cookies era el elemento LCP y solo se
    // pintaba tras arrancar el JS (~8s en móvil lento). ConsentService.hasDecided()
    // decide en servidor por la cookie de la petición (REQUEST_COOKIES), así que el
    // SSR trae el banner ya renderizado solo para quien no ha decidido — mismo
    // resultado que calculará el cliente al hidratar (sin mismatch, sin parpadeo).
    this.cookiesStatus();

    if (isPlatformBrowser(this.platformId)) {
      this.initializeApp();
      this.montarAsistente();

      // Registrar Swiper en tiempo ocioso: su bundle (~0,5MB con emoji/translate)
      // compite en la ventana crítica si se pide durante el arranque, inflando el TBT.
      // requestIdleCallback lo saca de esa ventana; los carruseles se inicializan unos
      // ms más tarde (scroll nativo entretanto, sin salto). Fallback a setTimeout.
      // Además, en móvil el home no tiene ningún <swiper-container> (los carruseles
      // son de desktop/ficha), así que solo precargamos si ya hay uno en el DOM;
      // ensureSwiper() sigue disponible bajo demanda para quien lo necesite luego.
      const bootSwiper = () => {
        if (document.querySelector('swiper-container')) {
          import('./utils/swiper-loader').then(m => m.ensureSwiper());
        }
      };
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(bootSwiper, { timeout: 2000 });
      } else {
        setTimeout(bootSwiper, 200);
      }

      // Los SDK de Facebook y Google ya no se cargan aquí: se piden en el momento
      // de usarlos (utils.loadFacebookSdk / loadGoogleSignIn), que es lo que evita
      // cargarlos sin consentimiento y para usuarios que nunca hacen login social.
      // this.menu.enable(true, 'menu');
      // this.menu.swipeGesture(false, 'menu');
      this.checkTheme();

      // La app NUNCA pide el permiso de notificaciones por su cuenta: fuera de un gesto
      // (y el antiguo «primer clic» lo perdía tras dos import() dinámicos de Firebase) el
      // navegador bloquea o esconde el aviso. Lo pide sólo el interruptor «En el navegador»
      // del modal de alertas (NotificacionesNavegadorService.activar). Aquí, con el permiso
      // YA concedido, se refresca el token en silencio al arrancar (en idle, para no meter
      // Firebase en la ventana crítica) y al iniciar sesión, que lo (re)envía aunque sea el
      // mismo: la cuenta puede ser otra y el token tiene que quedar ligado a la actual.
      const armToken = () => {
        sincronizarTokenNotificaciones({
          login$: this.event.userEvent$.pipe(
            filter((e: any) => e?.event === 'login'),
            takeUntilDestroyed(this.destroyRef),
          ),
          refrescar: (forzar) => this.refrescarTokenFcm(forzar),
        });
      };
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(armToken, { timeout: 2000 });
      } else {
        setTimeout(armToken, 200);
      }

      // listen() carga firebase/messaging (~46 KB raw) solo para onMessage: no tiene
      // sentido pedirlo en cada carga si no hay permiso de notificaciones concedido
      // (mayoría de visitas). Se difiere a idle y solo se dispara si ya hay permiso.
      const armListen = () => {
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          this.listen();
        }
      };
      if ('requestIdleCallback' in window) {
        (window as any).requestIdleCallback(armListen, { timeout: 2000 });
      } else {
        setTimeout(armListen, 200);
      }

      this.utils.deleteCookie('referer');
    }
    // this.getUser();
    // this.getStoresCategories()
  }

  // storesCategoriesLoading = false;
  // storesCategoriesLoaded: any;

  // async getStoresCategories() {
  //   if (this.stores?.length > 0 && this.categories?.length > 0) return; 

  //   // this.storesCategoriesLoading = true;

  //   // this.storesCategoriesLoaded = new Promise(async (res) => {
  //     // await Promise.all([
  //   if (this.stores?.length === 0) this.getStores();
  //   if (this.categories?.length === 0) this.getCategories();
  //     // ]);
  //   //   setTimeout(res, 50);
  //   // });

  //   // this.storesCategoriesLoading = false;
  // }


  getPageName() {
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.url.set(event.url);
        // La vista de página la emite PageTrackerService: aquí no se puede saber
        // si /algo/ es un chollo, una categoría o una tienda, y la llamada que
        // había (gtag('screen_view', ...)) usaba el nombre del evento como
        // comando de gtag, así que GA4 la descartaba entera.
        // La página de error no debe reescribir la canónica: se renderiza sobre la
        // URL que el usuario pidió (skipLocationChange) y apuntar la canónica a
        // /pagina-no-encontrada/ es lo que hacía que Search Console la declarase
        // como canónica de miles de ofertas reales.
        if (event.url.indexOf('/pagina-no-encontrada') === -1) {
          this.meta.createLinkForCanonicalURL();
        }

        this.utils.seUrlIosBanner(this.url());
      }
    });
  }

  /**
   * Síncrono a propósito. La versión anterior leía un flag de @capacitor/storage,
   * cuyo `isBrowser` llega por suscripción: si aún no había llegado devolvía null
   * y el banner reaparecía a quien ya había decidido.
   */
  cookiesStatus() {
    this.showCookies.set(!this.consent.hasDecided());
  }

  onConsent(prefs: McConsentPrefs) {
    this.consent.set(prefs);
    this.showCookies.set(false);
  }



  async checkTheme() {
    const stored = await this.storage.getItem('darkTheme');

    const applyDark = (dark: boolean) => {
      if (dark) {
        this.renderer.addClass(this.document.body, 'theme-dark');
      } else {
        this.renderer.removeClass(this.document.body, 'theme-dark');
      }
    };

    // Light by default; the system's dark mode no longer switches it («la version clara es por defecto»).
    applyDark(temaOscuroInicial(stored));
  }

  /**
   * Con el permiso de notificaciones YA concedido, obtiene el token FCM y lo registra en la
   * API para el usuario con sesión. `forzar` reenvía aunque sea el último enviado. No pide
   * permiso nunca: sin 'granted' (o sin sesión) sale sin cargar Firebase, porque con
   * 'default' `getToken()` llamaría a `Notification.requestPermission()` fuera de un gesto.
   */
  async refrescarTokenFcm(forzar = false): Promise<boolean> {
    if (!debePedirToken(apiNotificaciones()) || !this.session.mc?.user) return false;
    try {
      // Lazy load Firebase solo cuando se necesita (ahorra ~200-300KB inicial)
      const { initializeApp, getApps } = await import("firebase/app");
      const { getMessaging, getToken } = await import("firebase/messaging");

      // getApps(): NotificacionesNavegadorService también puede haberla inicializado.
      if (!this.firebaseInitialized && !getApps().length) {
        initializeApp(environment.firebase);
        this.firebaseInitialized = true;
      }

      const messaging = getMessaging();

      const currentToken = await getToken(messaging, { vapidKey: environment.firebase.vapidKey });

      if (currentToken) {
        this.fcmToken = currentToken;
        await enviarTokenFcm(this.session.mc, currentToken, { forzar });
      }
      return !!currentToken;
    } catch {
      // Silencioso: sin permiso de notificaciones no hay token, y no es un error de la app
      // (best-practices de Lighthouse: sin errores en consola).
      return false;
    }
  }

  async listen() {
    try {
      // Lazy load Firebase solo cuando se necesita
      const { initializeApp, getApps } = await import("firebase/app");
      const { getMessaging, onMessage } = await import("firebase/messaging");

      // getApps(): NotificacionesNavegadorService también puede haberla inicializado.
      if (!this.firebaseInitialized && !getApps().length) {
        initializeApp(environment.firebase);
        this.firebaseInitialized = true;
      }

      const messaging = getMessaging();

      // El payload no se pinta en ningún sitio: registrar el handler basta para que el
      // navegador no saque la notificación por defecto con la app en primer plano.
      onMessage(messaging, () => { /* nada que hacer con el mensaje */ });
    } catch {
      // Igual que arriba: si el navegador no soporta push, no hay nada que avisar.
    }
  }

}
